import React, { useState, useRef, useEffect } from 'react'; // useState used for search state
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import LottieAnimation from '../hooks/getLottie';
import { decreaseQuantity, addToCart, removeFromCart, setOpcionesProducto } from '../actions/cartActions';
import emptyAnimation from '../assets/empty.json';
import { buscarTituloCompleto } from '../utils/loaders';
import FormularioCotizacion from '../components/formularios/FormularioCotizacion';
import SEO from '../components/SEO';

let searchTimer;

const BANDAS = ['VHF', 'UHF'];
const TIPOS = ['Digital', 'Analógico'];

const trustSignals = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    text: 'Respuesta en menos de 24 horas',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    text: 'Sin costo ni compromiso',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    text: 'Asesoría personalizada incluida',
  },
];

function Cotizaciones() {
  const dispatch = useDispatch();
  const productosCarrito = useSelector((state) => state.cart);
  const uniqueCount = productosCarrito.cart.length;
  const opcionesProducto = productosCarrito.opciones || {};

  const handleOpcion = (productoId, campo, valor) => {
    const current = opcionesProducto[productoId] || {};
    const newVal = current[campo] === valor ? '' : valor;
    dispatch(setOpcionesProducto(
      productoId,
      campo === 'banda' ? newVal : (current.banda || ''),
      campo === 'tipo' ? newVal : (current.tipo || ''),
    ));
  };

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);

  const handleSearch = (text) => {
    clearTimeout(searchTimer);
    setSearchQuery(text);
    if (!text.trim()) { setSearchResults([]); return; }
    searchTimer = setTimeout(async () => {
      setSearchLoading(true);
      const res = await buscarTituloCompleto(text);
      setSearchResults(res.data || []);
      setSearchLoading(false);
    }, 300);
  };

  const handleAddToCart = (result) => {
    const productForCart = {
      producto_id: result.id,
      producto_titulo: result.titulo,
      producto_imagen_url: result.imagen_url,
      producto_descripcion: result.descripcion || '',
      categoria_nombre: result.categoria_nombre || '',
    };
    dispatch(addToCart(productForCart, 1));
    toast.success(`${result.titulo} añadido a la cotización`, {
      style: { background: '#0f274b', color: '#fff', fontWeight: '600', borderRadius: '12px' },
      iconTheme: { primary: '#00BEE7', secondary: '#0f274b' },
    });
    setSearchQuery('');
    setSearchResults([]);
    setSearchFocused(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stripHtmlTags = (input) => (input ? input.replace(/<\/?[^>]+(>|$)/g, '') : '');

  return (
    <main className="bg-gray-50 min-h-screen">
      <Toaster position="top-right" />
      <SEO
        title="Cotizaciones"
        description="Revisa y envía tu cotización de radios y accesorios profesionales en ATST Radiocom."
        canonicalPath="/cotizaciones"
      />

      {/* Hero banner */}
      <div className="w-full h-40 md:h-56 flex flex-col justify-center items-center relative overflow-hidden">
        <img
          src="https://dps2wxsijib8x.cloudfront.net/atst/BannerPagina.webp"
          alt="Cotizaciones ATST"
          className="object-cover w-full h-full absolute inset-0 z-0"
        />
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#0f274b]/95 via-[#0f274b]/80 to-[#0f274b]/50" />
        <div className="relative z-10 text-center px-4">
          <span className="inline-block bg-[#00BEE7]/20 border border-[#00BEE7]/40 text-[#00BEE7] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
            Mi carrito
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-black text-white tracking-tight">
            Mis Cotizaciones
          </h1>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Left: Cart items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-xl text-[#0f274b] border-l-4 border-[#00BEE7] pl-3">
                Productos
                {uniqueCount > 0 && (
                  <span className="ml-2 text-sm font-bold bg-[#00BEE7]/15 text-[#007fa0] px-2 py-0.5 rounded-full">
                    {uniqueCount}
                  </span>
                )}
              </h2>
            </div>

            {/* Buscador para agregar productos */}
            <div className="relative" ref={searchRef}>
              <div className={`flex items-center rounded-xl border transition-all duration-200 overflow-hidden ${searchFocused ? 'border-[#00BEE7]/70 shadow-md shadow-[#00BEE7]/10' : 'border-gray-200'} bg-white`}>
                <span className="pl-3 pr-2 shrink-0 text-gray-400">
                  {searchLoading ? (
                    <svg className="h-4 w-4 animate-spin text-[#00BEE7]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                    </svg>
                  )}
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  placeholder="Buscar y agregar más productos..."
                  className="w-full bg-transparent py-2.5 pr-3 outline-none border-none focus:ring-0 text-[#0f274b] placeholder-gray-400 text-sm font-medium"
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    className="pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Limpiar búsqueda"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Search dropdown */}
              {searchFocused && searchQuery && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-2xl z-30 overflow-hidden max-h-64 overflow-y-auto scroll-container">
                  {searchResults.map((resultado) => (
                    <button
                      type="button"
                      key={resultado.id}
                      onClick={() => handleAddToCart(resultado)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#0f274b]/5 transition-colors duration-150 w-full text-left"
                    >
                      <div className="w-10 h-10 shrink-0 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden">
                        <img src={resultado.imagen_url} alt={resultado.titulo} className="w-10 h-10 object-contain p-0.5" onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#0f274b] text-sm truncate">{resultado.titulo}</p>
                        <p className="text-gray-400 text-xs mt-0.5 truncate">{stripHtmlTags(resultado.descripcion).substring(0, 60)}</p>
                      </div>
                      <span className="shrink-0 text-xs font-extrabold text-[#00BEE7] bg-[#00BEE7]/10 px-2 py-1 rounded-full whitespace-nowrap">
                        + Agregar
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {searchFocused && searchQuery && !searchLoading && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-xl z-30 py-4 px-4 text-center">
                  <p className="text-gray-500 text-sm font-medium">Sin resultados para <span className="font-bold text-[#0f274b]">&ldquo;{searchQuery}&rdquo;</span></p>
                  <Link to="/productos" className="mt-1 text-[#00BEE7] font-bold text-xs hover:underline inline-block">
                    Ver todo el catálogo →
                  </Link>
                </div>
              )}
            </div>

            {productosCarrito.cart.length > 0 ? (
              <div className="space-y-4">
                {productosCarrito.cart.map((producto) => {
                  const pid = producto.product.producto_id;
                  const opciones = opcionesProducto[pid] || {};
                  const descripcionLimpia = stripHtmlTags(producto.product.producto_descripcion || '');

                  return (
                    <div
                      key={pid}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-[#00BEE7]/30 transition-all duration-200 overflow-hidden"
                    >
                      {/* Product main row */}
                      <div className="p-4 flex gap-4">
                        <Link
                          to={`/producto/${producto.product.producto_titulo}`}
                          state={{ id: pid }}
                          className="w-20 h-20 shrink-0 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden hover:border-[#00BEE7]/50 transition-colors"
                        >
                          <img
                            src={producto.product.producto_imagen_url}
                            alt={producto.product.producto_titulo}
                            className="w-full h-full object-contain p-1.5"
                            onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }}
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <Link
                              to={`/producto/${producto.product.producto_titulo}`}
                              state={{ id: pid }}
                              className="font-extrabold text-[#0f274b] text-sm leading-snug hover:text-[#007fa0] transition-colors line-clamp-2"
                            >
                              {producto.product.producto_titulo}
                            </Link>
                            <button
                              type="button"
                              aria-label="Eliminar producto"
                              onClick={() => dispatch(removeFromCart(pid))}
                              className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150 shrink-0"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          {descripcionLimpia && (
                            <p className="text-gray-400 text-xs mt-1 line-clamp-2 leading-relaxed">
                              {descripcionLimpia.substring(0, 100)}
                            </p>
                          )}
                          {/* Quantity stepper */}
                          <div className="flex items-center gap-2 mt-3">
                            <span className="text-xs text-gray-400 font-medium mr-1">Cantidad:</span>
                            <button
                              type="button"
                              aria-label="Reducir cantidad"
                              onClick={() => dispatch(decreaseQuantity(pid))}
                              className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#00BEE7] hover:text-[#00BEE7] hover:bg-[#00BEE7]/10 transition-all duration-150 text-base font-bold leading-none"
                            >
                              −
                            </button>
                            <span className="text-sm font-extrabold text-[#0f274b] min-w-[1.5rem] text-center">
                              {producto.quantity}
                            </span>
                            <button
                              type="button"
                              aria-label="Aumentar cantidad"
                              onClick={() => dispatch(addToCart(producto.product, 1))}
                              className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#00BEE7] hover:text-[#00BEE7] hover:bg-[#00BEE7]/10 transition-all duration-150 text-base font-bold leading-none"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Band / Type selectors */}
                      <div className="border-t border-gray-50 px-4 py-3 bg-gray-50/50 flex flex-wrap gap-x-5 gap-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider">Banda:</span>
                          <div className="flex gap-1.5">
                            {BANDAS.map((banda) => (
                              <button
                                key={banda}
                                type="button"
                                onClick={() => handleOpcion(pid, 'banda', banda)}
                                className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-all duration-150 ${
                                  opciones.banda === banda
                                    ? 'bg-[#0f274b] text-white border-[#0f274b]'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-[#00BEE7]/60 hover:text-[#00BEE7]'
                                }`}
                              >
                                {banda}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider">Tipo:</span>
                          <div className="flex gap-1.5">
                            {TIPOS.map((tipo) => (
                              <button
                                key={tipo}
                                type="button"
                                onClick={() => handleOpcion(pid, 'tipo', tipo)}
                                className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-all duration-150 ${
                                  opciones.tipo === tipo
                                    ? 'bg-[#0f274b] text-white border-[#0f274b]'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-[#00BEE7]/60 hover:text-[#00BEE7]'
                                }`}
                              >
                                {tipo}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 flex flex-col items-center text-center">
                <div className="w-28 h-28">
                  <LottieAnimation animationData={emptyAnimation} />
                </div>
                <p className="text-gray-500 font-semibold mt-2 text-sm">Tu carrito está vacío</p>
                <p className="text-gray-400 text-xs mt-1">Usa el buscador o explora el catálogo.</p>
                <Link
                  to="/productos"
                  className="mt-5 bg-[#0f274b] hover:bg-[#00BEE7] text-white hover:text-[#0f274b] font-extrabold text-sm px-6 py-2.5 rounded-full transition-all duration-200"
                >
                  Explorar productos
                </Link>
              </div>
            )}

            {/* Trust signals */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
              <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">¿Por qué cotizar con nosotros?</p>
              {trustSignals.map((signal) => (
                <div key={signal.text} className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#00BEE7]/10 shrink-0">
                    {signal.icon}
                  </span>
                  <span className="font-medium">{signal.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Quote form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-24">
              <div className="bg-[#0f274b] px-6 py-5">
                <h3 className="font-extrabold text-white text-lg">Solicitar cotización</h3>
                <p className="text-blue-200 text-sm mt-1">
                  Completa el formulario y un asesor te contactará a la brevedad.
                </p>
              </div>
              <div className="p-6">
                <FormularioCotizacion />
              </div>
            </div>
          </div>

        </div>
      </div>
    </main>
  );
}

export default Cotizaciones;
